import { animate, group, query, style, transition, trigger } from "@angular/animations";
import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory, LiveAnnouncer } from "@angular/cdk/a11y";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  output,
  viewChild,
  input,
} from "@angular/core";
import { FADE_TIME, SLIDE_ANIMATION } from "src/app/animations";
import { FmsService } from "src/app/services/fms.service";
import { SimulationAgeComponent } from "../../simulation-age/simulation-age.component";
import { FmsPipe } from "../../../../shared/pipes/fms.pipe";
import { ButtonModule } from "@storeblocks/button-ng";

enum AnnouncementKey {
  Open = "simulationParamsActivator.announcement.open",
  Close = "simulationParamsActivator.announcement.close",
}

const BACKDROP_SELECTOR = ".backdrop";
const PANEL_SELECTOR = ".panel";

@Component({
  selector: "app-simulation-params-panel",
  templateUrl: "./simulation-params-panel.component.html",
  styleUrls: ["./simulation-params-panel.component.scss"],
  animations: [
    trigger("slideUpDown", [
      transition(":enter", [
        group([
          query(BACKDROP_SELECTOR, [style({ opacity: 0 }), animate(FADE_TIME, style({ opacity: 1 }))]),
          query(PANEL_SELECTOR, [
            style({ transform: "translateY(100%)" }),
            animate(SLIDE_ANIMATION, style({ transform: "translateY(0)" })),
          ]),
        ]),
      ]),
      transition(":leave", [
        group([
          query(BACKDROP_SELECTOR, [animate(FADE_TIME, style({ opacity: 0 }))]),
          query(PANEL_SELECTOR, [animate(SLIDE_ANIMATION, style({ transform: "translateY(100%)" }))]),
        ]),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SimulationAgeComponent, FmsPipe, ButtonModule],
})
export class SimulationParamsPanelComponent implements OnDestroy {
  private readonly focusTrapFactory: ConfigurableFocusTrapFactory = inject(ConfigurableFocusTrapFactory);
  private readonly fmsService: FmsService = inject(FmsService);
  private readonly liveAnnouncer: LiveAnnouncer = inject(LiveAnnouncer);

  private focusTrap!: ConfigurableFocusTrap;

  public readonly open = input(false);

  public readonly panelRef = viewChild.required<ElementRef>("panelRef");
  public readonly close = output<void>();
  public readonly closed = output<void>();

  public ngOnDestroy(): void {
    this.liveAnnouncer.clear();

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }

  public onAnimationEvent(): void {
    if (this.open()) {
      this.focusTrap = this.focusTrapFactory.create(this.panelRef().nativeElement);
    } else {
      this.closed.emit();
    }
  }

  public announce(): Promise<void> {
    const openText = this.fmsService.getText(AnnouncementKey.Open);
    const closeText = this.fmsService.getText(AnnouncementKey.Close);

    return this.liveAnnouncer.announce(this.open() ? openText : closeText);
  }
}
